// Показ данных: статусы, остатки, полосы, пузыри, строки списков. SMY-66

import { useState, type ReactNode } from 'react'
import {
  formatUnit, pluralize, sourcesNote, UNIT_LABELS,
  pastSearchNote,
  type Balance, type Source, type StepStatus, type UnitId, type UnitLabel,
} from '@smyslika/core'

import { Icon, Tile, type IconName } from './Icon.tsx'
import { Mark } from './Mark.tsx'

export function Caps({ children }: { children: ReactNode }) {
  return <div className="caps">{children}</div>
}

/**
 * Статусы результата — три и только три: подтверждено, спорно, не найдено.
 * Служебные цвета в продукте больше нигде не появляются.
 */
export type ResultStatus = 'ok' | 'disputed' | 'none' | 'danger'

const STATUS_COLOR: Record<ResultStatus, { dot: string; text: string; soft: string }> = {
  ok: { dot: 'var(--status-ok)', text: 'var(--status-ok-text)', soft: 'var(--status-ok-soft)' },
  disputed: { dot: 'var(--status-warn)', text: 'var(--status-warn-text)', soft: 'var(--status-warn-soft)' },
  none: { dot: 'var(--status-none)', text: 'var(--ink-45)', soft: 'var(--surface-alt)' },
  danger: { dot: 'var(--status-danger)', text: 'var(--danger-text)', soft: 'var(--surface-alt)' },
}

export function StatusDot({ status }: { status: ResultStatus }) {
  return <span className="status-dot" style={{ background: STATUS_COLOR[status].dot }} />
}

export function StatusBadge({ status, children }: { status: ResultStatus; children: ReactNode }) {
  const c = STATUS_COLOR[status]
  return (
    <span className="status-badge" style={{ background: c.soft, color: c.text }}>
      {children}
    </span>
  )
}



/**
 * Полоса лимита. Цвет решает сервер, а не клиент: порог «пора
 * предупреждать» живёт в конфигурации, и второе число здесь означало бы
 * второй источник правды (см. Balance.low в ядре).
 */
export function ProgressBar({ value, max, low }: { value: number; max: number; low?: boolean }) {
  const share = max <= 0 ? 0 : Math.max(0, Math.min(1, value / max))
  return (
    <div className="progress" role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={max}>
      <div className={`progress__fill${low ? ' progress__fill--warn' : ''}`} style={{ width: `${share * 100}%` }} />
    </div>
  )
}

/** Пузырь чата. Аватар ассистента — тот же знак, что в логотипе. */
export function Bubble({ from, children }: { from: 'user' | 'assistant'; children: ReactNode }) {
  return (
    <div className={`bubble-row bubble-row--${from}`}>
      {from === 'assistant' ? (
        <span className="bubble__avatar">
          <Mark height={18} />
        </span>
      ) : null}
      <div className={`bubble bubble--${from}`}>{children}</div>
    </div>
  )
}

/** Строка списка: иконка, название, мета, шеврон. Экраны 06, 09, 10, 11. */
export function Row({
  icon,
  tile,
  tileColor,
  title,
  meta,
  right,
  chevron,
  onClick,
}: {
  icon?: IconName
  /** Цветная метка вместо иконки — так выглядят строки источников и проектов. */
  tile?: boolean
  /** Цвет метки: у проекта это цвет темы, у источника — нейтральный. */
  tileColor?: string
  title: ReactNode
  meta?: ReactNode
  right?: ReactNode
  chevron?: boolean
  onClick?: () => void
}) {
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag className="row" {...(onClick ? { type: 'button' as const, onClick } : {})}>
      {tile ? <Tile color={tileColor ?? 'var(--line-soft)'} /> : null}
      {icon != null && !tile ? <Icon name={icon} size={20} /> : null}
      <span className="row__body">
        <span className="row__title">{title}</span>
        {meta != null ? <span className="row__meta">{meta}</span> : null}
      </span>
      {right}
      {chevron ? (
        <span className="row__chevron">
          <Icon name="chevron" size={18} />
        </span>
      ) : null}
    </Tag>
  )
}

/**
 * Этап выполнения — экран 05.
 *
 * Выполненное называется в прошедшем времени, идущее и предстоящее — в
 * настоящем. Две формулировки на этап приходят из ядра (RunStep.doneLabel);
 * нет второй — годится первая.
 */
export function Step({
  status,
  label,
  doneLabel,
  detail,
  progress,
}: {
  status: StepStatus
  label: string
  doneLabel?: string
  detail?: string
  /** Доля 0–1 у идущего этапа. */
  progress?: number
}) {
  const done = status === 'done'
  // `failed` — свой вид, а не «всё остальное». Раньше он схлопывался в
  // `pending`, и остановленный этап выглядел ровно как ненаступивший: пустое
  // кольцо и бледная подпись. На отказе по содержанию это читалось как
  // зависший виджет — работа кончилась, а «Отвечаю» так и стоит в очереди
  // (найдено на проде 06.09.2026).
  const marker = done ? 'done' : status === 'active' ? 'active' : status === 'failed' ? 'failed' : 'pending'

  return (
    <div className={`step step--${marker}`}>
      <span className={`step__marker step__marker--${marker}`}>
        {done ? <Icon name="tick" size={12} strokeWidth={3.2} /> : null}
        {marker === 'failed' ? <Icon name="close" size={11} strokeWidth={3} /> : null}
      </span>
      <span className="step__body">
        <span className="step__label">{done ? (doneLabel ?? label) : label}</span>
        {detail != null ? <span className="step__detail">{detail}</span> : null}
        {status === 'active' && progress != null ? (
          <span className="step__bar">
            <span className="step__bar-fill" style={{ width: `${Math.max(0, Math.min(1, progress)) * 100}%` }} />
          </span>
        ) : null}
      </span>
    </div>
  )
}

const SOURCE: UnitLabel = { one: 'источник', few: 'источника', many: 'источников' }

/**
 * Источники — одной свёрнутой строкой под тем, что ими доказано. SMY-117
 *
 * Сначала их показывали колонкой справа, потом первыми тремя под ответом.
 * Оба раза мешало одно: ссылки занимали место, сравнимое с самим ответом, а
 * читает их меньшинство — те, кто пришёл проверять. Строка «Источники · 7»
 * отвечает на вопрос большинства («они вообще есть?») и раскрывается тем,
 * кому нужен список.
 *
 * `details` вместо своего состояния: раскрытие переживает перерисовку, его
 * умеет клавиатура, и программа чтения с экрана называет его сама.
 */
export function Sources({
  sources,
  title = 'Источники',
}: {
  sources: readonly Source[]
  title?: string
}) {
  if (sources.length === 0) return null

  return (
    <details className="sources-line">
      <summary>
        <span className="caps">{title}</span>
        <span className="sources__count">{sources.length}</span>
        <span className="row__chevron">
          <Icon name="chevron" size={16} />
        </span>
      </summary>
      <div className="sources__list">
        {sources.map((s, i) => (
          <SourceRow key={`${s.url}-${i}`} source={s} />
        ))}
      </div>
    </details>
  )
}

/**
 * Значок сайта в строке источника.
 *
 * Берётся ПРЯМО С САЙТА, а не из сервиса фавиконок Google или Яндекса. Разница
 * не в удобстве: сервис узнавал бы, какие результаты человек увидел, по всем
 * запросам сразу и без единого клика — то есть собирал бы у себя ровно то, что
 * этот продукт обещает не отдавать. Запрос к самому источнику знает про него
 * один сайт, тот, на который человек и так в шаге от перехода, а `no-referrer`
 * не сообщает ему даже откуда пришли.
 *
 * `/favicon.ico` есть не у всех: часть сайтов отдаёт 404 (проверено 06.09.2026
 * на выдаче — rbc.ru и vc.ru отдают HTML-страницу с кодом 404). Разбирать ради
 * них `<link rel=icon>` со страницы значило бы тянуть саму страницу. Не
 * получилось — остаётся цветная плитка, то самое место под фавикон, которое
 * в макете и нарисовано; строка выглядит так же, просто без значка.
 */
function Favicon({ url }: { url: string }) {
  const [failed, setFailed] = useState(false)

  let origin: string | null = null
  try {
    const parsed = new URL(url)
    if (parsed.protocol === 'https:' || parsed.protocol === 'http:') origin = parsed.origin
  } catch {
    // Ссылка не разобралась — значка не будет, и это не повод ронять строку.
  }

  if (origin == null || failed) return <Tile size={22} />

  return (
    <img
      className="sources__favicon"
      src={`${origin}/favicon.ico`}
      alt=""
      width={22}
      height={22}
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  )
}

function SourceRow({ source }: { source: Source }) {
  return (
    <a
      className="sources__row sources__row--link"
      href={source.url}
      target="_blank"
      rel="noreferrer noopener"
    >
      <Favicon url={source.url} />
      <span className="sources__body">
        <span className="sources__title">{source.title}</span>
        {source.publishedAt != null ? <span className="sources__meta">{source.publishedAt}</span> : null}
      </span>
      <span className="row__chevron">
        <Icon name="chevron" size={16} />
      </span>
    </a>
  )
}

/**
 * Одна серая строка вместо карточки хода работы. SMY-116
 *
 * Пока запуск идёт, этапы и есть содержание экрана: человек смотрит на них,
 * потому что больше смотреть не на что. У ГОТОВОГО ответа всё наоборот —
 * этапы занимают больше места, чем сам ответ, и после перечитывания ленты
 * оказываются под ним, то есть подпись стоит ниже подписанного.
 *
 * Остаётся то, ради чего на них смотрят задним числом: сколько заняло и
 * списалось ли. Строка стоит НАД ответом — как подпись у цитаты.
 *
 * Формулировка про списание без глагола («списание: 1 вопрос») намеренно:
 * «списался вопрос», «списалось изображение» — разный род, и одной фразой на
 * все единицы это не сказать.
 */
export function RunLine({
  durationMs,
  charged,
  unit,
  sourcesAt,
  pastSearchAt,
}: {
  durationMs?: number
  charged: boolean
  unit: UnitId
  /**
   * На какое число собраны источники. Пусто — их не было.
   *
   * Встаёт СПРАВА той же строки (07.09.2026). Раньше это была плашка под
   * ответом, пятая строка вокруг него; здесь она занимает пустое место в уже
   * существующей подписи и ничего не добавляет к высоте экрана.
   */
  sourcesAt?: string
  /**
   * Ответ собран из прошлого поиска без нового — момент того поиска (SMY-497,
   * `pastSearchAt` ленты). Встаёт на месте даты источников: своих источников у
   * такого ответа нет, а цены и места в нём — из того поиска.
   */
  pastSearchAt?: string
}) {
  // Двоеточие, а не глагол: «списался вопрос» и «списалось изображение» —
  // разный род, и одной фразой на все единицы это не сказать.
  //
  // «С лимита» — с 07.09.2026 и во всём кабинете: «списалось» без предмета
  // человек читает как списание денег, а деньги здесь не списываются вовсе.
  const charge = charged ? `Списалось с лимита: ${formatUnit(1, unit)}` : 'Без списания лимита'
  const took = durationMs != null ? `Выполнилось за ${seconds(durationMs)}. ` : ''

  return (
    <p className="run-line">
      <span>
        {took}
        {charge}
      </span>
      {sourcesAt != null ? (
        <span className="run-line__sources">{sourcesNote(sourcesAt)}</span>
      ) : pastSearchAt != null ? (
        <span className="run-line__sources">{pastSearchNote(pastSearchAt)}</span>
      ) : null}
    </p>
  )
}

/** «7 с», «1,5 с», «меньше секунды» — время, а не точность до миллисекунды. */
function seconds(ms: number): string {
  if (ms < 1000) return 'меньше секунды'
  const n = ms / 1000
  // До десяти секунд десятая доля читается, дальше — шум: «43,7 с» никому не
  // говорит больше, чем «44 с». Ровные секунды пишутся без хвоста: «7,0 с»
  // выглядит точностью, которой нет.
  if (n >= 10 || Math.round(n * 10) % 10 === 0) return `${Math.round(n)} с`
  return `${n.toFixed(1).replace('.', ',')} с`
}
